import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, startWith, switchMap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { AppDataService, IcdCode } from './../../../../core/services/app-data.service';
import { isPartnerCode, runIcdRules } from './../../../../core/engines/icd-rules-engine/icd-rules';
import { IcdFinding } from './../../../../core/engines/icd-rules-engine/icd-rules-models';
import { IcdCodeDateDialogComponent } from './../icd-code-date-dialog/icd-code-date-dialog.component';
import { todayIsoDay } from 'src/app/shared/util/icd-coding-context.util';
import { IcdChipView, icdChipView, withIcdReference } from 'src/app/shared/util/icd-chip-state.util';

@Component({
  selector: 'app-icd-code-selector',
  templateUrl: './icd-code-selector.component.html',
  styleUrls: ['./icd-code-selector.component.scss']
})
export class IcdCodeSelectorComponent implements OnInit, OnChanges {
  @Input() initialCodes: IcdCode[] = [];
  @Input() isRequired: boolean = false;
  /** Patient sex ('M'/'F') — enables the sex-restriction rule. Omit and it stays silent. */
  @Input() patientSex: string | null = null;
  /** Patient age in years — enables the age-restriction rule. Omit and it stays silent. */
  @Input() patientAge: number | null = null;
  /**
   * The date a newly added code is dated from — the encounter being captured (admission date,
   * discharge date, note date).
   *
   * Falls back to TODAY when the caller has no contextual date. A clinician adding a code by
   * hand is documenting it now, so today is a truthful answer and a far better default than
   * leaving the field empty — an undated code is invisible to any date-based report and has to
   * be chased down later.
   *
   * That is deliberately NOT how the AI path behaves: it back-dates a code to the note it
   * quoted, because it is describing something written weeks ago. The two differ because the
   * question differs — "when was this documented" has a different answer for each.
   *
   * Either way the clinician can correct or clear it by double-clicking the chip (or, on a
   * touch screen, tapping and holding it - see TouchEditDirective).
   *
   * Must be 'YYYY-MM-DD'.
   */
  @Input() defaultOnsetDate: string | null = null;
  @Output() codesChanged = new EventEmitter<IcdCode[]>();
  @ViewChild('inputElement') inputElement!: ElementRef<HTMLInputElement>;

  /**
   * Coding-rule findings for the current selection (§ core/engines/icd-rules-engine).
   *
   * ⚠ ADVISORY. Nothing here blocks a save or reorders anything — a clinician sees the reason
   * and decides. The MIT's flags are non-exhaustive and the codebook is a 2021 snapshot, so a
   * rule that blocked would eventually be wrong with no way to be overruled.
   */
  findings: IcdFinding[] = [];
  overallStatus: 'valid' | 'warning' | 'error' = 'valid';

  /**
   * Per-chip visual state, index-aligned with `icdChips`.
   *
   * Recomputed by `revalidate()` rather than derived in the template — see the note there.
   * Shared with the patient file card via `icdChipView`, so the same code cannot read as clean
   * on one screen and flagged on the other.
   *
   * ⚠ INVARIANT: length-matched with `icdChips` at all times. Every mutation of `icdChips`
   * (`addChip`, `removeChip`, `dropChip`, `editChip`, `makePrimary`, `updateChips`) calls
   * `revalidate()` in the same synchronous block, so no change-detection pass can ever observe
   * the two out of step — which is why the template indexes this without a null guard. A future
   * mutation that skips `revalidate()` breaks the template, not just the findings.
   */
  chipViews: IcdChipView[] = [];

  /**
   * The shared tooltip plus this component's own affordance, index-aligned with `icdChips`.
   *
   * Assembled in TypeScript rather than concatenated in the template: an escape sequence inside
   * an Angular expression is a thing that either works or silently renders `\n` as two literal
   * characters, and this tooltip is the only place a long description is readable in full.
   */
  chipTooltips: string[] = [];

  icdControl = new FormControl();
  filteredIcdCodes: Observable<IcdCode[]>;
  existingCodes = new Set<string>();
  icdChips: IcdCode[] = [];
  readonly separatorKeysCodes: number[] = [13, 188]; // Enter and comma key codes
  addOnBlur = true;

  constructor(private appDataService: AppDataService, private dialog: MatDialog) {
    this.filteredIcdCodes = this.icdControl.valueChanges.pipe(
      startWith(''),
      switchMap(value => this.filterIcdCodes(value))
    );
  }

  ngOnInit(): void {
    this.updateChips();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialCodes']) {
      this.updateChips();
    }
  }

  private updateChips(): void {
    // Initialize the chips with any initial codes passed in
    this.icdChips = this.initialCodes.slice();
    this.existingCodes.clear();
    this.initialCodes.forEach(code => {
      this.existingCodes.add(code.code);
    });
    this.revalidate();
  }

  /**
   * Re-runs the coding rules over the current selection.
   *
   * Codes already on the record arrive as `{ id, code, description, pmbCodes }` — the server
   * does not send the MIT flags back — so each chip is re-joined to the reference list before
   * validating. Without that, an existing invalid code would look clean and only a NEWLY added
   * one would be flagged, which is precisely backwards: the old ones have had longer to reach a
   * claim.
   */
  private revalidate(): void {
    const enriched = this.icdChips.map(chip =>
      withIcdReference(chip, this.appDataService.performDirectLookup(chip.code, 'icd')));

    const result = runIcdRules(enriched, {
      sex: this.patientSex,
      age: this.patientAge,
    });
    this.findings = result.findings;
    this.overallStatus = result.overallStatus;

    // Chip states are computed HERE, once per change, not read through a template method.
    // `severityOf(i)` filtered the findings array on every change-detection cycle for every
    // chip; `icdChipView` does strictly more work than that, so calling it the same way would
    // have made a shared vocabulary cost more than the duplication it replaced.
    //
    // Built from `enriched`, not `icdChips`: the MIT flags live only on the codebook row. The
    // server DOES send `pmbCodes` — often as an explicit null — which is why `withIcdReference`
    // has to let the codebook win it back rather than merely fill a gap.
    this.chipViews = enriched.map((code, index) => icdChipView(
      code,
      index,
      this.findings.filter(f => f.codeIndex === index),
    ));
    this.chipTooltips = this.chipViews.map(
      view => `${view.tooltip}\n\nDouble-click or tap and hold to set the onset date`,
    );
  }

  /** Findings attached to one chip, for the inline marker. */
  findingsFor(index: number): IcdFinding[] {
    return this.findings.filter(f => f.codeIndex === index);
  }

  severityOf(index: number): string | null {
    const own = this.findingsFor(index);
    if (own.some(f => f.severity === 'error')) return 'error';
    if (own.some(f => f.severity === 'warning')) return 'warning';
    return own.length ? 'info' : null;
  }

  tooltipFor(index: number): string {
    return this.findingsFor(index).map(f => f.message).join('\n');
  }

  /**
   * Moves a code to the primary position.
   *
   * Position is meaning — `codes[0]` is the primary diagnosis — so the fix for most findings is
   * a reorder, and offering it beside the reason is what turns a warning into something a
   * clinician can act on in one click.
   */
  makePrimary(index: number): void {
    if (index <= 0 || index >= this.icdChips.length) return;
    const [moved] = this.icdChips.splice(index, 1);
    this.icdChips.unshift(moved);
    this.codesChanged.emit(this.icdChips);
    this.revalidate();
  }

  /**
   * Drag-and-drop reordering.
   *
   * The same act as `makePrimary`, done directly: dragging a code to the front makes it the
   * primary diagnosis. Sequence is not cosmetic here — it is what the ICD-001, ICD-009 and
   * ICD-011 rules are about, and what a medical scheme reads off the claim.
   *
   * ⚠ `revalidate()` MUST run after a drop. The findings are position-dependent, so a reorder
   * that left them untouched would leave the panel describing the previous arrangement — the
   * clinician fixes the problem and the warning stays, or worse, creates one and sees nothing.
   */
  dropChip(event: CdkDragDrop<IcdCode[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    moveItemInArray(this.icdChips, event.previousIndex, event.currentIndex);
    this.codesChanged.emit(this.icdChips);
    this.revalidate();
  }

  private filterIcdCodes(value: string): Observable<IcdCode[]> {
    return this.appDataService.searchCodes(value, 'icd').pipe(
      debounceTime(1000), // Adjust the debounce time as needed (in milliseconds)
      distinctUntilChanged()
    );
  }

  resetInput(): void {
    setTimeout(() => {
      this.inputElement.nativeElement.value = '';
      this.icdControl.reset();
    });
  }

  async addChip(code: IcdCode): Promise<void> {
    // ⚠ THE DUPLICATE GUARD IS BACK ON. It was commented out, so the same code could be added
    // twice and reach a claim twice. Rule ICD-008 reports a duplicate, but reporting one the
    // picker just created is closing the door after the fact — the rule now exists for
    // duplicates that arrive from elsewhere (an import, a merge, an accepted suggestion).
    // External causes and morphology codes are the exception: each one sits directly after the
    // code it belongs to, so two injuries with the same cause carry that cause twice.
    if (this.existingCodes.has(code.code) && !isPartnerCode(code)) {
      this.resetInput();
      return;
    }

    // Narrowed to what the RECORD stores. The MIT flags are reference data, not patient data —
    // `revalidate()` re-joins them from the code list rather than persisting a copy that would
    // go stale the next time the codebook is rebuilt.
    this.icdChips.push({
      code: code.code,
      description: code.description,
      pmbCodes: code.pmbCodes,
      // Dated from the encounter being captured, falling back to today. `todayIsoDay()` reads
      // the LOCAL calendar — `toISOString().slice(0,10)` would date a code added before 02:00
      // SAST to yesterday, a bug that only ever shows up in the early morning.
      onsetDate: this.defaultOnsetDate || todayIsoDay(),
    });
    this.existingCodes.add(code.code);
    this.codesChanged.emit(this.icdChips);
    this.revalidate();
    this.resetInput();
    return;
  }

  /**
   * Double-click a chip to set or change its onset date. On a touch screen the same edit is
   * reached by double-tapping or tapping and holding, which appTouchEdit on the chip turns
   * into the dblclick this handler is bound to.
   *
   * A gesture rather than a visible button: the chip row is already crowded with a remove
   * control and a rule marker, and adding a third target makes the common action — removing a
   * wrong code — harder to hit. The date is shown on the chip, so there is something to
   * double-click that looks like it holds a value.
   */
  editDate(index: number): void {
    const chip = this.icdChips[index];
    if (!chip) return;

    const ref = this.dialog.open(IcdCodeDateDialogComponent, {
      width: '420px',
      data: { code: chip.code, description: chip.description, onsetDate: chip.onsetDate || null },
    });

    ref.afterClosed().subscribe((result) => {
      // `undefined` is Cancel; `{ onsetDate: null }` is a deliberate clear. Treating the two
      // the same would make Cancel wipe the date.
      if (!result) return;
      this.icdChips[index] = { ...chip, onsetDate: result.onsetDate };
      this.codesChanged.emit(this.icdChips);
    });
  }

  removeChip(index: number): void {
    const code = this.icdChips[index].code;
    this.icdChips.splice(index, 1);
    if (!this.icdChips.some(c => c.code === code)) this.existingCodes.delete(code);
    this.codesChanged.emit(this.icdChips);
    this.revalidate();
  }

  editChip(chip: IcdCode, event: any): void {
    const index = this.icdChips.indexOf(chip);
    if (index !== -1) {
      this.icdChips[index].description = event.value;
      this.codesChanged.emit(this.icdChips);
      this.revalidate();
    }
  }
}